// @ts-strict-ignore
import React, { useState, useEffect, useMemo, useCallback, ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { navStore } from '../nav/Nav'
import { navV2Store } from '../nav-v2/store'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Card } from './card'
import { Filter } from '../Filter'
import { DateRangeSelect } from './DateRangeSelect'
import { HideColumns } from './HideColumns'
import { axiosPatch, axiosDestroy } from '../../util/api'
import { setDateRange } from '../../actions/user'
import { useUser } from '../../hooks/useUser'
import { DefaultSelected } from '../../packages/blueprint-components/src/common'
import { TableHooks, useTableHooks } from '../../packages/blueprint-components/src/useTableHooks'
import { MyHTMLTable } from '../../packages/blueprint-components/src/HTMLTable'
import { Search } from '../../packages/blueprint-components/src/Search'
import { CardHeader } from './CardHeader'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { isDefined } from '../../util/helpers'
import { useOnProfileChange } from '../../hooks/useOnProfileChange'
import { Column } from '../../types/table'
import { fetchCustomFilters, useFieldVisibility } from './VirtualizedTableWithoutBulkActions'
import { Toasts } from '../toaster/Toast'
import { isNavV2Enabled } from '../../util/nav-version'

export const columnVisibility = ({ allColumns, hiddenColumns = [] }) => {
    return allColumns.filter((item) => !hiddenColumns.includes(item.key))
}

export interface TableProps<T, Extra extends object> {
    style?: object
    load?: any
    tableHooks?: TableHooks<T>
    filters?: any
    search?: boolean
    searchPlaceholder?: any
    searchIconTooltipContent?: ReactNode
    headerComponent?: any
    columns: Column<T, Extra>[]
    name?: any
    modelName?: any
    reloadOnProfileChange?: any
    dateSelect?: boolean
    showPerPage?: boolean
    perPage?: any

    bulkActions?: (v: any) => ReactNode
    columnSelection?: boolean
    fetchData?: any
    selectedValues?: any
    onSelectChange: any
    selectKey?: any
    mutableFields?: string[]
    accordion?: any
    accordionRaw?: any
    empty?: any
    hideIfNone?: any
    className?: ClassValue
    cardClassName?: ClassValue
    canSelect?: any
    canSelectFromAllPages?: boolean
    children?: any
    setDateRangeToUrl?: any

    stickyFooter?: any
    toolbarClassName?: ClassValue
    leadingActions?: any
    leftActions?: any
    otherActions?: any
    trailingActions?: any
    showHeader?: any
    hoverCheckbox?: boolean

    defaultActiveFilters?: any
    height?: number
    condensedToolbar?: boolean
    floatingBulkActions?: boolean
    striped?: boolean
    rowDividers?: boolean
    onRowClickItem?: (item: T) => void
}

export const Table = <T, Extra extends object>({
    load,
    tableHooks,
    filters,
    search, // boolean - whether or not to show search bar
    searchPlaceholder,
    searchIconTooltipContent,
    headerComponent,
    columns,
    name,
    modelName,
    reloadOnProfileChange = true,
    dateSelect, // boolean - whether or not to show date select
    showPerPage = true,
    perPage = 15,

    bulkActions,
    columnSelection = true, // boolean - whether or not to show column selection
    fetchData = {}, // array - if bulk actions has Dropdown modal send in the functions for fetching the dropdown data in array (ex. CampaignsTable)
    selectedValues,
    onSelectChange,
    selectKey = 'id',
    mutableFields,
    accordion,
    accordionRaw,
    empty,
    hideIfNone = false,
    className = 'my-4',
    cardClassName,
    canSelect,
    canSelectFromAllPages = true,
    children = null,

    stickyFooter = true,
    toolbarClassName,
    leadingActions,
    leftActions,
    otherActions,
    trailingActions,
    showHeader,
    defaultActiveFilters = {},
    height,
    hoverCheckbox = false,
    condensedToolbar = false,
    floatingBulkActions = false,
    striped = true,
    rowDividers = false,
    onRowClickItem,
}: TableProps<T, Extra>): JSX.Element | null => {
    const dispatch = useDispatch()
    const { pathname, search: locationSearch } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(locationSearch), [locationSearch])
    const flattenForNavV2 =
        useNavV2 &&
        (pathname.startsWith('/admin') ||
            pathname === '/combined-history' ||
            pathname === '/promotions' ||
            pathname === '/smart-campaigns' ||
            pathname === '/recipes' ||
            pathname === '/segments')
    const { hiddenColumns, handleColumnSelect } = useFieldVisibility(name)
    const [entriesPerPage, setEntriesPerPage] = useState(perPage)
    const [customFilters, setCustomFilters] = useState([])
    const { user, activeProfile } = useUser()
    const localStorageHook = useLocalStorage('preferences', name, activeProfile)

    if (!tableHooks) {
        tableHooks = useTableHooks(load)
    }

    const visibleColumns = useMemo(
        () =>
            columnVisibility({
                allColumns: columns,
                hiddenColumns: hiddenColumns,
            }),
        [columns.length, hiddenColumns, tableHooks.items, tableHooks.loading],
    )

    useOnProfileChange(() => (reloadOnProfileChange ? tableHooks.reload() : null))

    // let defaultActiveFilters = {}
    useEffect(() => {
        // if (from && to) {
        //     const fromDate = new Date(from)
        //     const toDate = new Date(to)

        //     const pathname = location.pathname
        //     updateUrl(pathname, fromDate, toDate)
        // }

        if (!!filters) {
            fetchCustomFilters(name, null, setCustomFilters)
        }
    }, [])

    const hasWidgets =
        filters ||
        dateSelect ||
        !!bulkActions ||
        columnSelection ||
        search ||
        !!leadingActions ||
        !!leftActions ||
        !!otherActions ||
        !!trailingActions

    const saveCustomFilter = (filterName, filters) => {
        axiosPatch('custom-filters', {
            name: filterName,
            tableName: name.toLowerCase(),
            filters: filters || tableHooks.params.filter,
        }).then((_) => {
            fetchCustomFilters(name, null, setCustomFilters)
        })
    }

    const editCustomFilter = (filterId, payload) => {
        axiosPatch('custom-filters', {
            id: filterId,
            name: customFilters[0].name,
            tableName: name.toLowerCase(),
            payload,
        }).then(() => {
            // Toaster?
            // fetchCustomFilters(name, null, setCustomFilters)
        })
    }

    const deleteCustomFilter = (filterId: number) => {
        return axiosDestroy(`custom-filters/${filterId}`)
            .then((_) => {
                const filterName = customFilters.find((item) => item.id == filterId)?.name
                Toasts.success(`${filterName} deleted`)

                fetchCustomFilters(name, null, setCustomFilters)
            })
            .catch((err) => {
                Toasts.error(err.message)
            })
    }

    const applyProfilePreferencesFromLocalStorage = (): Promise<number | void> => {
        const savedProfilePreferences = localStorageHook.getForActiveProfile()
        if (!savedProfilePreferences) {
            return Promise.resolve()
        }

        let perPage = 15
        const exists = Object(savedProfilePreferences).hasOwnProperty(name)
        if (exists) {
            const data = savedProfilePreferences[name]
            if (isDefined(data, 'perPage')) {
                perPage = data.perPage
            }
        }

        setEntriesPerPage(perPage)
        return Promise.resolve()
    }

    const persistPerPageToLocalStorage = (perPage: number) => {
        localStorageHook.set({
            perPage,
        })
    }
    const setPerPage = useCallback(
        (entries: number) => {
            persistPerPageToLocalStorage(entries)
            applyProfilePreferencesFromLocalStorage()
        },
        [entriesPerPage, activeProfile],
    )

    const queryByDate = ({ from, to }) => {
        dispatch(setDateRange({ from, to }))
        // if (setDateRangeToUrl) {
        //     updateUrl(location.pathname, from, to)
        // }
        tableHooks.setParams((prev) => ({
            ...prev,
            from,
            to,
        }))
    }

    // On Table init
    useEffect(() => {
        if (!!activeProfile) {
            if (!tableHooks.commit) {
                if (tableHooks.items.length != entriesPerPage && tableHooks.setParams) {
                    tableHooks.setParams((prev) => ({ ...prev, perPage: entriesPerPage }))
                }
                loadProfilePreferencesFromLocalStorage().then(() => {
                    // Some read-only tables provide static rows rather than a full
                    // useTableHooks result. There is nothing to commit for them.
                    tableHooks.setCommit?.(true)
                })
            }
        }
    }, [activeProfile?.profile_id])

    const loadProfilePreferencesFromLocalStorage = async () => {
        await applyProfilePreferencesFromLocalStorage()
        return Promise.resolve(true)
    }

    const hasFloatingBanner =
        floatingBulkActions &&
        (selectedValues?.all ||
            (selectedValues?.safeList?.length ?? 0) > 0 ||
            (selectedValues?.blockList?.length ?? 0) > 0)

    const tableMemo = useMemo(() => {
        return (
            <MyHTMLTable
                canSelectFromAllPages={canSelectFromAllPages}
                accordion={accordion}
                accordionRaw={accordionRaw}
                empty={empty}
                disableDefaultHtmlTableWrapperStyle={false}
                className="mj-table"
                paginationClassname="mx-4 mb-4"
                columns={visibleColumns}
                {...tableHooks}
                selectedValues={selectedValues}
                onSelectChange={onSelectChange}
                selectKey={selectKey}
                canSelect={canSelect}
                showPerPage={showPerPage}
                perPage={entriesPerPage}
                setPerPage={setPerPage}
                stickyHeader
                stickyFooter={stickyFooter}
                stickyFooterOffset={hasFloatingBanner ? 56 : 0}
                onRowClickItem={onRowClickItem}
                height={height}
                hoverCheckbox={hoverCheckbox}
                striped={striped}
                rowDividers={rowDividers}
            />
        )
    }, [
        tableHooks.loading,
        tableHooks.items,
        selectedValues,
        accordion,
        accordionRaw,
        visibleColumns,
        entriesPerPage,
        setPerPage,
        hasFloatingBanner,
        empty,
    ])

    if (hideIfNone && tableHooks.items.length === 0) {
        return null
    }

    return (
        <div className={classNames(flattenForNavV2 && className === 'my-4' ? 'my-0' : className)}>
            <Card
                className={classNames(
                    'd-flex flex-column p-0 bp4-card-rounded w-full',
                    flattenForNavV2 && 'flat-table-card !rounded-none !shadow-none',
                    cardClassName,
                )}
                style={{ boxShadow: flattenForNavV2 ? 'none' : undefined }}
            >
                {name && showHeader && <CardHeader title={name} component={headerComponent} />}
                <div>
                    {children}
                    {hasWidgets && (
                        <div
                            className={classNames(
                                'flex h-[49px] items-center gap-2 px-2',
                                toolbarClassName,
                            )}
                        >
                            {leadingActions}
                            {dateSelect && (
                                <DateRangeSelect
                                    minDate={user?.start_date ? new Date(user.start_date) : null}
                                    value={[tableHooks.params?.from, tableHooks.params?.to]}
                                    onChange={([from, to]) => {
                                        if (from && to) {
                                            if (onSelectChange) {
                                                onSelectChange(DefaultSelected)
                                            }
                                            queryByDate({ from, to })
                                        }
                                    }}
                                />
                            )}
                            {filters ? (
                                <Filter
                                    filters={filters}
                                    customFilters={customFilters}
                                    saveFilter={saveCustomFilter}
                                    deleteCustomFilter={deleteCustomFilter}
                                    editCustomFilter={editCustomFilter}
                                    onChange={(filters) => {
                                        if (onSelectChange) {
                                            onSelectChange(DefaultSelected)
                                        }
                                        tableHooks.setFilter(filters)
                                    }}
                                    defaultActiveFilters={defaultActiveFilters}
                                    disableSavedFilters={name == 'Audit Logs'}
                                />
                            ) : null}
                            {leftActions}
                            <div className="flex items-center ml-auto">
                                {search && (
                                    <Search
                                        className="mx-2 w-full"
                                        width={290}
                                        style={{ width: '290px' }}
                                        loading={tableHooks.loading}
                                        placeholder={searchPlaceholder}
                                        iconTooltipContent={searchIconTooltipContent}
                                        onSearch={(query) => {
                                            if (onSelectChange) {
                                                onSelectChange(DefaultSelected)
                                            }
                                            tableHooks.setQuery(query)
                                        }}
                                    />
                                )}
                                {otherActions}
                                {bulkActions &&
                                    !floatingBulkActions &&
                                    bulkActions({
                                        columns,
                                        selectKey,
                                        fetchData,
                                        params: tableHooks.params,
                                        reload: (clearSelection) => {
                                            tableHooks.reload()
                                            if (clearSelection) {
                                                onSelectChange(DefaultSelected)
                                            }
                                        },
                                        name,
                                        modelName,
                                        disabled: !(
                                            selectedValues &&
                                            (selectedValues?.safeList.length > 0 ||
                                                selectedValues?.blockList.length > 0 ||
                                                selectedValues?.all) &&
                                            mutableFields
                                        ),
                                        mutableFields,
                                        selectedValues,
                                        setSelectedValues: onSelectChange,
                                    })}
                                {columnSelection && (
                                    <HideColumns
                                        className="mx-2 rounded-lg"
                                        columns={columns}
                                        hiddenColumns={hiddenColumns}
                                        setHiddenColumns={handleColumnSelect}
                                    />
                                )}
                                {trailingActions}
                            </div>
                        </div>
                    )}
                    {tableMemo}
                </div>
            </Card>
            {floatingBulkActions && bulkActions && (
                <BulkActionsBanner
                    selectedValues={selectedValues}
                    onSelectChange={onSelectChange}
                    bulkActionsProps={{
                        columns,
                        selectKey,
                        fetchData,
                        params: tableHooks.params,
                        reload: (clearSelection) => {
                            tableHooks.reload()
                            if (clearSelection) {
                                onSelectChange(DefaultSelected)
                            }
                        },
                        name,
                        modelName,
                        mutableFields,
                        selectedValues,
                        setSelectedValues: onSelectChange,
                        disabled: false,
                    }}
                    bulkActions={bulkActions}
                />
            )}
        </div>
    )
}

// ── BulkActionsBanner ─────────────────────────────────────────────────────────

const BulkActionsBanner = observer(
    ({
        selectedValues,
        onSelectChange,
        bulkActionsProps,
        bulkActions,
    }: {
        selectedValues: any
        onSelectChange: any
        bulkActionsProps: any
        bulkActions: (v: any) => ReactNode
    }) => {
        const hasSelection =
            selectedValues &&
            (selectedValues.all ||
                selectedValues.safeList?.length > 0 ||
                selectedValues.blockList?.length > 0)
        const [visible, setVisible] = useState(false)

        useEffect(() => {
            if (hasSelection) {
                const id = requestAnimationFrame(() => setVisible(true))
                return () => cancelAnimationFrame(id)
            } else {
                setVisible(false)
            }
        }, [hasSelection])

        const count = selectedValues?.all
            ? `All (${selectedValues.selectedCount})`
            : (selectedValues?.selectedCount ?? 0)

        const { search } = useLocation()
        const useNavV2 = isNavV2Enabled(search)
        const navOffset =
            navStore.open && navStore.isPinned ? (useNavV2 ? navV2Store.widthStyle : '16em') : '0'

        // Always render so bulkActions(…) is called unconditionally every render.
        // The banner is hidden off-screen via translate-y when nothing is selected.
        return (
            <div
                className={`fixed bottom-0 right-0 h-12 border-t border-[#DBE2E8]/80 bg-white/85 backdrop-blur-md z-[15] transition-transform duration-[180ms] ease-out shadow-[0_-8px_24px_rgba(14,41,71,0.09),0_-1px_4px_rgba(14,41,71,0.06)] ${hasSelection && visible ? 'translate-y-0' : 'translate-y-full'}`}
                style={{ left: navOffset }}
                aria-hidden={!hasSelection}
            >
                <div className="max-w-5xl mx-auto w-full h-full flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5 text-[12px] text-[#7289A3]">
                        <span className="size-1.5 rounded-full bg-[#3391FF]" />
                        <span>
                            <span className="font-medium text-[#0E2947]">{count}</span> item
                            {(selectedValues?.selectedCount ?? 0) !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            type="button"
                            onClick={() => onSelectChange(DefaultSelected)}
                            className="text-[#94A3B8] hover:text-[#0E2947] transition-colors underline-offset-2 hover:underline text-[11px]"
                        >
                            Clear
                        </button>
                    </div>
                    <div className="flex items-center gap-2">{bulkActions(bulkActionsProps)}</div>
                </div>
            </div>
        )
    },
)
